import { BaseService, ServiceError, ValidationSchema } from './BaseService';
import { 
  callScriptRepository, 
  CallScript 
} from '../repositories/CallScriptRepository';

/**
 * Input type for creating a call script
 */
export interface CreateCallScriptInput {
  company_id: string;
  name: string;
  script_type: CallScript['script_type'];
  content: string;
  active?: boolean;
}

/**
 * Input type for updating a call script
 */
export interface UpdateCallScriptInput {
  name?: string;
  script_type?: CallScript['script_type'];
  content?: string;
  active?: boolean;
}

/**
 * Service for managing call scripts
 * Handles business logic and validation for call script operations
 */
export class CallScriptService extends BaseService {
  /**
   * Validation schema for creating a call script
   */
  private createSchema: ValidationSchema = {
    company_id: {
      required: true,
      type: 'string',
      message: 'Company ID is required'
    },
    name: {
      required: true,
      type: 'string',
      min: 3,
      max: 100,
      message: 'Name must be between 3 and 100 characters'
    },
    script_type: {
      required: true,
      type: 'string',
      custom: (value) => {
        const validTypes = ['greeting', 'inquiry', 'claim', 'closing', 'followup', 'other'];
        return validTypes.includes(value) || 'Invalid script type';
      }
    },
    content: {
      required: true,
      type: 'string',
      min: 10,
      message: 'Content must be at least 10 characters'
    }
  };

  /**
   * Get all scripts for a company
   */
  async getScripts(companyId: string, activeOnly: boolean = false): Promise<CallScript[]> {
    try {
      return await callScriptRepository.findByCompany(companyId, activeOnly);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get scripts by type
   */
  async getScriptsByType(
    companyId: string,
    scriptType: CallScript['script_type']
  ): Promise<CallScript[]> {
    try {
      return await callScriptRepository.findByType(companyId, scriptType);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get a single script by ID
   */
  async getScript(id: string): Promise<CallScript> {
    try {
      const script = await callScriptRepository.findById(id);
      if (!script) {
        throw ServiceError.notFound('Call script', id);
      }
      return script;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Create a new call script
   */
  async createScript(input: CreateCallScriptInput): Promise<CallScript> {
    try {
      // Validate input
      this.validate(input, this.createSchema);

      // Sanitize data
      const sanitized = {
        company_id: input.company_id,
        name: this.sanitizeString(input.name) || '',
        script_type: input.script_type,
        content: this.sanitizeString(input.content) || '',
        active: input.active !== undefined ? input.active : true
      };

      // Create script
      const script = await callScriptRepository.create(sanitized);

      // Emit event
      await this.emit('script.created', { script });

      return script;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update an existing call script
   */
  async updateScript(id: string, input: UpdateCallScriptInput): Promise<CallScript> {
    try {
      // Check if script exists
      const existing = await callScriptRepository.findById(id);
      if (!existing) {
        throw ServiceError.notFound('Call script', id);
      }

      // Sanitize data
      const sanitized: Partial<CallScript> = {};
      if (input.name) sanitized.name = this.sanitizeString(input.name) || undefined;
      if (input.content) sanitized.content = this.sanitizeString(input.content) || undefined;
      if (input.script_type) sanitized.script_type = input.script_type;
      if (input.active !== undefined) sanitized.active = input.active;

      // Update script
      const updated = await callScriptRepository.update(id, sanitized);

      // Emit event
      await this.emit('script.updated', { script: updated });

      return updated;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Toggle script active status
   */
  async toggleActive(id: string): Promise<CallScript> {
    try {
      const script = await callScriptRepository.toggleActive(id);
      await this.emit('script.toggled', { script });
      return script;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Delete a call script
   */
  async deleteScript(id: string): Promise<void> {
    try {
      // Check if script exists
      const script = await callScriptRepository.findById(id);
      if (!script) {
        throw ServiceError.notFound('Call script', id);
      }

      // Delete script
      await callScriptRepository.delete(id);

      // Emit event
      await this.emit('script.deleted', { scriptId: id });
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get count of scripts by type
   */
  async getScriptCountByType(
    companyId: string,
    scriptType: CallScript['script_type']
  ): Promise<number> {
    try {
      return await callScriptRepository.countByType(companyId, scriptType);
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Export singleton instance
export const callScriptService = new CallScriptService();
